/**
 * Customer e-mail preferences: which automated customer mails (gepland
 * onderhoud, storingen incl. modem offline) a CRM contact receives.
 *
 * Zoho CRM is the source of truth: the flags are the "mail ..." checkboxes on
 * the Zoho contact (ticked = receives) and the backend writes every change to
 * Zoho first and only then to its local mirror. Two callers:
 * - the staff toggles on the customer page (`updateContactEmailPreferences`,
 *   authenticated);
 * - the public preferences page behind the opt-out links in the mails
 *   (`getEmailPreferences` & co, authorised by the signed token in the link).
 */

export type EmailCategory = 'onderhoud' | 'storingen'

export const EMAIL_CATEGORIES: readonly EmailCategory[] = ['onderhoud', 'storingen']

/** The checkboxes to change, as in Zoho: true = ontvangt, false = afgemeld. Absent categories are left alone. */
export type EmailPreferenceChanges = Partial<Record<EmailCategory, boolean>>

export interface EmailPreferenceCategory {
  key: EmailCategory
  label: string
  labelEn: string
  description: string
  descriptionEn: string
  /** The Zoho checkbox: true = ontvangt, false = afgemeld. */
  receives: boolean
}

export interface EmailPreferencesView {
  contact: {
    zoho_id: string
    voornaam: string
    achternaam: string
    email: string
    bedrijf: string | null
  }
  /** Zoho's global "Email Opt Out": nothing is sent while this is set, whatever the categories say. */
  emailOptOut: boolean
  categories: EmailPreferenceCategory[]
}

/** Permission node for the staff-side toggles (backend: /modules/companies/contacts). */
export const CONTACTPERSONEN_NODE = '/frontend/modules/klanten/contactpersonen'

export class EmailPreferencesApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'EmailPreferencesApiError'
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  })

  if (!response.ok) {
    let message = 'Er is iets misgegaan'
    try {
      const body = await response.json()
      message = Array.isArray(body?.message) ? body.message.join(', ') : (body?.message || message)
    }
    catch {
      message = response.statusText || message
    }
    throw new EmailPreferencesApiError(message, response.status)
  }

  return response.json()
}

// ---- public, token-authorised (the links in the mails)

export function getEmailPreferences(token: string): Promise<EmailPreferencesView> {
  return request(`/api/email-preferences/${encodeURIComponent(token)}`)
}

export function updateEmailPreferences(token: string, changes: EmailPreferenceChanges): Promise<EmailPreferencesView> {
  return request(`/api/email-preferences/${encodeURIComponent(token)}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  })
}

export function unsubscribeEmailCategory(token: string, category: EmailCategory): Promise<EmailPreferencesView> {
  return request(`/api/email-preferences/${encodeURIComponent(token)}/unsubscribe/${category}`, { method: 'POST' })
}

// ---- staff, session-authorised

export function updateContactEmailPreferences(zohoId: string, changes: EmailPreferenceChanges): Promise<EmailPreferencesView> {
  return request(`/api/crm/contacts/${encodeURIComponent(zohoId)}/email-preferences`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  })
}
